import './App.css';
import React, { useContext, useEffect } from "react";
import { AuthProvider } from "./utils/auth.js";
import { AuthContext } from "./utils/auth.js";
import { Route, Switch, BrowserRouter, Redirect, withRouter } from "react-router-dom";
import { Toaster } from 'react-hot-toast';

import Dashboard from './screens/Dashboard.js';
import Account from './screens/Account.js';
import Api from './screens/Api.js';
import Billing from './screens/Billing.js';
import DataExtraction from './screens/DataExtraction.js';
import DocumentClassification from './screens/DocumentClassification.js';
import Login from './screens/Login.js';
import Admin from './screens/Admin.js';
import ForgotPassword from './screens/ForgotPassword.js';
import Register from './screens/Register.js';
import EmailVerificationNotice from './screens/VerifyEmail.js';
import SubscriptionIframe from './SubscriptionIframe.js';
import DemoIframe from './DemoIframe.js';
import ServerHealth from './screens/Admin/ServerHealth.js';

import { initializeTagManager } from "./utils/gtm.js";
import { loadTawkTo } from "./utils/tawkTo.js";
import AdminDevToolbar from './components/AdminDevToolbar.js';
import AdminDevToolsSync from './components/AdminDevToolsSync.js';
import { DevSidebarProvider } from './context/DevSidebarContext.js';
import { isAdminEmail } from './utils/adminUsers.js';

const BadgeComponent = ({ text }) => (
  <div className="bg-blue-500 text-white text-xs font-bold py-2 px-4 rounded-full">
    {text}
  </div>
);

export default function App() {
  const environment = process.env.REACT_APP_ENV;

  useEffect(() => {
    initializeTagManager();
    loadTawkTo();
  }, []);

  if (environment === "pricing") {
    return (
      <SubscriptionIframe />
    );
  }

  if (environment === "demo") {
    return (
      <DemoIframe />
    );
  }

  return (
    <AuthProvider>
      <BrowserRouter>
        <DevSidebarProvider>
          <AdminDevToolsSync />
          <div className='h-full'>
            <div>
            <Toaster
              position="top-right"
              reverseOrder={false}
            />
            </div>

            <Switch>
              <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
              <Route exact path="/login" component={Login} />
              <Route exact path="/forgot" component={ForgotPassword} />
              <Route exact path="/register" component={Register} />
              <PrivateRouteEmailVerify exact path="/verify-email" component={EmailVerificationNotice} />
              <PrivateRoute exact path="/dashboard" component={withRouter(Dashboard)} />
              <ProtectedRoute exact path="/admin/health" component={withRouter(ServerHealth)} />
              <ProtectedRoute exact path="/admin/:section?" component={withRouter(Admin)} />
              <PrivateRoute path="/extractions" component={withRouter(DataExtraction)} />
              <PrivateRoute path="/classifications" component={withRouter(DocumentClassification)} />
              <PrivateRoute exact path="/account" component={withRouter(Account)} />
              <PrivateRoute exact path="/api" component={withRouter(Api)} />
              <PrivateRoute exact path="/billing" component={withRouter(Billing)} />
            </Switch>
          </div>
          {environment === "test" && (
            <div className="fixed bottom-4 right-4 z-50">
              <BadgeComponent text="test app" />
            </div>
          )}
          {environment !== 'production' && <AdminDevToolbar />}
        </DevSidebarProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

function PrivateRoute({ component: RouteComponent, ...rest }) {
  const { currentUser, isEmailVerified } = useContext(AuthContext);

  return (
    <Route
      {...rest}
      render={routeProps =>
        currentUser ? (
          isEmailVerified ? (
            <RouteComponent {...routeProps} />
          ) : (
            <Redirect to="/verify-email" />
          )
        ) : (
          (() => {
            const fullPath = routeProps.location.pathname + (routeProps.location.search || '');
            localStorage.setItem('intendedPath', fullPath);
            window.location.href = "/login";
            return null;
          })()
        )
      }
    />
  );
}

function PrivateRouteEmailVerify({ component: RouteComponent, ...rest }) {
  const { currentUser, isEmailVerified } = useContext(AuthContext);

  return (
    <Route
      {...rest}
      render={routeProps =>
        currentUser ? (
          isEmailVerified ? (
            <Redirect to="/dashboard" />
          ) : (
            <RouteComponent {...routeProps} />
          )
        ) : (
          (() => {
            const fullPath = routeProps.location.pathname + (routeProps.location.search || '');
            localStorage.setItem('intendedPath', fullPath);
            window.location.href = "/login";
            return null;
          })()
        )
      }
    />
  );
}


const ProtectedRoute = ({ component: Component, ...rest }) => {
  const { currentUser } = useContext(AuthContext);

  return (
    <Route
      {...rest}
      render={props => {
        if (!currentUser) {
          const fullPath = props.location.pathname + (props.location.search || '');
          localStorage.setItem('intendedPath', fullPath);
          return <Redirect to="/login" />;
        }

        if (!isAdminEmail(currentUser.email)) {
          return <Redirect to="/dashboard" />;
        }

        return <Component {...props} />;
      }}
    />
  )
}